import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  api,
  clearTokens,
  getAccessToken,
  getCurrentOrgId,
  getRefreshToken,
  setCurrentOrgId,
  setTokens,
} from "../lib/api";
import i18n, { SUPPORTED_LOCALES } from "../i18n";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setCurrentOrgIdState] = useState(getCurrentOrgId());
  const [loading, setLoading] = useState(true);
  const fetchInFlight = useRef(false);

  const refreshMe = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setMemberships([]);
      setOrgs([]);
      setLoading(false);
      return null;
    }
    if (fetchInFlight.current) return null;
    fetchInFlight.current = true;
    try {
      const data = await api.me();
      setUser(data.user);
      setMemberships(data.memberships || []);
      setOrgs(data.orgs || []);

      // Sync i18n with the user's persisted locale on every /me refresh.
      // Skipping when languages already match avoids redundant React re-renders.
      const userLocale = data.user?.locale;
      if (
        userLocale &&
        SUPPORTED_LOCALES.includes(userLocale) &&
        i18n.language !== userLocale
      ) {
        i18n.changeLanguage(userLocale).catch(() => {});
      }

      const activeIdx = (data.memberships || []).findIndex(
        (m) => m.status === "active",
      );
      const stored = getCurrentOrgId();
      const storedValid =
        stored && (data.orgs || []).some((o) => o && o.id === stored);
      if (storedValid) {
        setCurrentOrgIdState(stored);
      } else if (activeIdx >= 0 && data.orgs[activeIdx]) {
        const next = data.orgs[activeIdx].id;
        setCurrentOrgId(next);
        setCurrentOrgIdState(next);
      } else {
        setCurrentOrgId(null);
        setCurrentOrgIdState(null);
      }
      return data;
    } catch (err) {
      // 401 → tokens stale, treat as signed-out
      if (err?.response?.status === 401) {
        clearTokens();
        setUser(null);
        setMemberships([]);
        setOrgs([]);
        setCurrentOrgId(null);
        setCurrentOrgIdState(null);
      }
      return null;
    } finally {
      fetchInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const signIn = useCallback(
    async ({ email, password }) => {
      const res = await api.login({ email, password });
      setTokens(res.tokens);
      await refreshMe();
      return res;
    },
    [refreshMe],
  );

  const signUp = useCallback(
    async ({ email, password, full_name, org_name, ...company }) => {
      // `company` carries the optional bin / legal_address / city / phone the
      // registration form collected; the backend persists them onto the org.
      const res = await api.signup({
        email,
        password,
        full_name,
        org_name,
        ...company,
      });
      setTokens(res.tokens);
      if (res.org?.id) {
        setCurrentOrgId(res.org.id);
        setCurrentOrgIdState(res.org.id);
      }
      await refreshMe();
      return res;
    },
    [refreshMe],
  );

  const signInWithEcp = useCallback(
    async (payload) => {
      const res = await api.ecpLoginOrRegister(payload);
      // "registered" / "logged_in" carry tokens → establish the session.
      // "requested" carries no tokens (awaiting admin approval) — the caller
      // renders the pending state and we leave the signed-out state intact.
      if (res.tokens) {
        setTokens(res.tokens);
        if (res.org?.id) {
          setCurrentOrgId(res.org.id);
          setCurrentOrgIdState(res.org.id);
        }
        await refreshMe();
      }
      return res;
    },
    [refreshMe],
  );

  const signOut = useCallback(async () => {
    const refresh_token = getRefreshToken();
    if (refresh_token) {
      try {
        await api.logout(refresh_token);
      } catch {
        // logout is idempotent; ignore network errors
      }
    }
    clearTokens();
    setCurrentOrgId(null);
    setUser(null);
    setMemberships([]);
    setOrgs([]);
    setCurrentOrgIdState(null);
  }, []);

  const switchOrg = useCallback((orgId) => {
    setCurrentOrgId(orgId);
    setCurrentOrgIdState(orgId);
  }, []);

  const currentOrg = useMemo(() => {
    if (!currentOrgId) return null;
    return orgs.find((o) => o && o.id === currentOrgId) || null;
  }, [orgs, currentOrgId]);

  const currentMembership = useMemo(() => {
    if (!currentOrgId) return null;
    return memberships.find((m) => m.org_id === currentOrgId) || null;
  }, [memberships, currentOrgId]);

  const value = useMemo(
    () => ({
      user,
      memberships,
      orgs,
      currentOrg,
      currentOrgId,
      currentMembership,
      role: currentMembership?.role || null,
      loading,
      refreshMe,
      signIn,
      signInWithEcp,
      signUp,
      signOut,
      switchOrg,
    }),
    [
      user,
      memberships,
      orgs,
      currentOrg,
      currentOrgId,
      currentMembership,
      loading,
      refreshMe,
      signIn,
      signInWithEcp,
      signUp,
      signOut,
      switchOrg,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
